import { Annotation, Column, Group } from '@studio/design-system/form';
import { Mediator } from '@studio/design-system/input';
import * as Select from '@studio/design-system/select';
import { ControlDefaults } from '@studio/models/control';
import { Headers, toHeaders, ViscaIpDefaults } from '@studio/models/control/visca-ip';
import type { Transport as M_Transport } from '@studio/models/net';
import { toPort } from '@studio/models/net';
import { toAccessor } from '@studio/react-utils';
import { useLatestFn } from '@studio/react-utils/hooks';
import { toString } from '@tryiris-ai/js-types';
import { defaults, upperFirst } from 'lodash-es';
import type { ReactElement, SyntheticEvent } from 'react';
import { Slugs } from './constants.js';
import type { ViscaIpProps } from './types.js';
import { Advanced, Placeholder } from '#~/common/editor/index.js';
import { Port } from '#~/device/components/port/index.js';
import { Transport } from '#~/device/components/transport/index.js';

/** `<ViscaIp>` settings. */
export const ViscaIp = ({
  settings,
  advanced = true,
  ...props
}: ViscaIpProps & { advanced?: boolean }): ReactElement => {
  const current = defaults({}, settings.value, ViscaIpDefaults, ControlDefaults);

  const handleHostChange = useLatestFn((value: string, event: SyntheticEvent) => {
    const host = toString(value);

    if (host && host !== current.host) {
      settings({ ...current, host });
    } else {
      event.preventDefault();
    }
  });

  const handleTransportChange = useLatestFn((transport: M_Transport) => {
    if (transport !== current.transport) {
      settings({ ...current, transport });
    }
  });

  const handlePortChange = useLatestFn((value: number) => {
    const port = toPort(value);

    if (port && port !== current.port) {
      settings({ ...current, port });
    }
  });

  const handleHeadersChange = useLatestFn((nextValue: string) => {
    const headers = toHeaders(nextValue);

    if (headers && headers !== current.headers) {
      settings({ ...current, headers });
    }
  });

  return (
    <Column {...props}>
      <Group annotation={<Annotation.Required />} label='IP address'>
        <Mediator
          onCommit={handleHostChange}
          placeholder={Placeholder.host}
          value={current.host}
          variant={!current.host ? 'error' : undefined}
        />
      </Group>

      <hr className='mt-8' />

      <Advanced slug={Slugs.advanced}>
        <Column>
          <Group label='Net protocol'>
            <Transport transport={toAccessor(current.transport, handleTransportChange)} />
          </Group>

          <Group label='Port'>
            <Port port={toAccessor(current.port, handlePortChange)} />
          </Group>

          {advanced && (
            <Group label='Visca headers'>
              <Select.Root onValueChange={handleHeadersChange} value={current.headers}>
                <Select.Trigger>
                  <Select.Value />
                </Select.Trigger>

                <Select.Content>
                  {[Headers.Auto, Headers.Standard, Headers.Extended].map((µ) => (
                    <Select.Item key={µ} value={µ}>
                      {upperFirst(µ)}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Group>
          )}
        </Column>
      </Advanced>
    </Column>
  );
};
